import { FC, Fragment, useEffect, useState } from 'react';
import { GoogleMap, InfoWindowF, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer, Cluster } from '@googlemaps/markerclusterer';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Loading } from '@/components';
import { libraries } from '@/components/map/MapHelpers';
import { ResidenceType } from '@/api/mutations/useRegistrySearch';
import { registryColumns, RESULT_ZOOM } from './registryConfig';

const StyledMap = styled.div`
  ${tw`flex relative h-full w-full md:rounded-xl`}

  .googleMap {
    ${tw`md:rounded-xl`}
  }

  .gm-style-mtc,
  .gm-style-cc {
    ${tw`hidden`}
  }
`;

const InfoContent = styled.div`
  ${tw`flex flex-col px-1 py-0.5 min-w-[220px] max-w-[300px]`};
`;

const InfoTitle = styled.div`
  ${tw`text-[13px] font-semibold text-slate-800 leading-snug`};
`;

const InfoSubtitle = styled.div`
  ${tw`text-[11px] text-slate-500 mt-0.5`};
`;

const InfoDivider = styled.div`
  ${tw`h-px bg-slate-200 my-2`};
`;

const InfoGrid = styled.div`
  ${tw`grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 items-baseline`};
`;

const InfoLabel = styled.span`
  ${tw`text-[11px] text-slate-500 whitespace-nowrap`};
`;

const InfoValue = styled.span`
  ${tw`text-xs font-medium text-slate-800 text-right`};
`;

interface Props {
  residences: ResidenceType[];
  center: { lat: number; lng: number };
  zoom: number;
  availableColumns?: typeof registryColumns;
}

const mapId = import.meta.env.VITE_API_GOOGLE_MAPS_ID;

const popupColumnsFrom = (columns: typeof registryColumns) =>
  columns.filter(
    (column) => column.priority !== 'low' && column.name !== 'address'
  );

// Brand-styled cluster bubble: translucent halo + solid green core with a
// white ring; grows with the cluster size
const clusterIcon = (count: number) => {
  const size = count < 100 ? 44 : count < 1000 ? 54 : 64;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60">
    <circle cx="30" cy="30" r="29" fill="#357D42" opacity="0.2"/>
    <circle cx="30" cy="30" r="25" fill="#357D42" opacity="0.35"/>
    <circle cx="30" cy="30" r="20" fill="#357D42"/>
    <circle cx="30" cy="30" r="20" fill="none" stroke="#ffffff" stroke-width="1.8" opacity="0.9"/>
  </svg>`;

  return {
    url: `data:image/svg+xml;base64,${btoa(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
};

// Matching single-residence pin: brand-green teardrop with a white core
const pinIcon = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 34">
    <path d="M12 1C6 1 1.2 5.8 1.2 11.8c0 7.3 9.3 19.5 10.3 20.8a0.6 0.6 0 0 0 1 0c1-1.3 10.3-13.5 10.3-20.8C22.8 5.8 18 1 12 1z"
      fill="#357D42" stroke="#1e3f27" stroke-width="0.8"/>
    <path d="M12 2.2c-5.3 0-9.6 4.3-9.6 9.6 0 3.1 2 7.4 4.2 11.1-1.3-3-2.2-6-2.2-8.4 0-5.9 3.4-9.9 7.6-12.3z"
      fill="#ffffff" opacity="0.18"/>
    <circle cx="12" cy="11.8" r="4.4" fill="#ffffff"/>
    <circle cx="12" cy="11.8" r="4.4" fill="none" stroke="#1e3f27" stroke-width="0.5" opacity="0.4"/>
  </svg>`;

  return {
    url: `data:image/svg+xml;base64,${btoa(svg)}`,
    scaledSize: new google.maps.Size(30, 42),
    anchor: new google.maps.Point(15, 42),
  };
};

const clusterRenderer = {
  render: ({ count, position }: Cluster) =>
    new google.maps.Marker({
      position,
      icon: clusterIcon(count),
      label: {
        text: count.toLocaleString('sv-SE'),
        color: '#ffffff',
        fontSize: count < 1000 ? '12px' : '11px',
        fontWeight: '600',
      },
      zIndex: google.maps.Marker.MAX_ZINDEX + count,
    }),
};

const RegistryMap: FC<Props> = ({
  residences,
  center,
  zoom,
  availableColumns = registryColumns,
}) => {
  const popupColumns = popupColumnsFrom(availableColumns);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_API_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
    language: 'sv',
    version: 'quarterly',
  });

  const [selectedResidence, setSelectedResidence] =
    useState<ResidenceType | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Imperative markers + clusterer: nearby markers collapse into numbered
  // bubbles so large result sets stay fast to render
  useEffect(() => {
    if (!map) return;

    const valid = residences.filter(
      (residence) =>
        typeof residence.wgs84lat === 'number' &&
        typeof residence.wgs84lon === 'number'
    );

    const icon = pinIcon();
    const markers = valid.map((residence) => {
      const marker = new google.maps.Marker({
        position: { lat: residence.wgs84lat, lng: residence.wgs84lon },
        title: String(residence.address ?? ''),
        icon,
      });
      marker.addListener('click', () => setSelectedResidence(residence));
      return marker;
    });

    const clusterer = new MarkerClusterer({
      map,
      markers,
      renderer: clusterRenderer,
    });

    return () => {
      clusterer.clearMarkers();
      clusterer.setMap(null);
    };
  }, [map, residences]);

  // After a search, bring every marker into view
  useEffect(() => {
    if (!map) return;

    const valid = residences.filter(
      (residence) =>
        typeof residence.wgs84lat === 'number' &&
        typeof residence.wgs84lon === 'number'
    );
    if (!valid.length) return;

    if (valid.length === 1) {
      map.setCenter({ lat: valid[0].wgs84lat, lng: valid[0].wgs84lon });
      map.setZoom(RESULT_ZOOM);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    valid.forEach((residence) =>
      bounds.extend({ lat: residence.wgs84lat, lng: residence.wgs84lon })
    );
    map.fitBounds(bounds, 48);
  }, [map, residences]);

  if (!isLoaded) return <Loading />;

  return (
    <StyledMap>
      <GoogleMap
        mapContainerClassName="googleMap"
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={zoom}
        onLoad={(loadedMap) => setMap(loadedMap)}
        onUnmount={() => setMap(null)}
        options={{
          mapId,
          clickableIcons: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {selectedResidence && (
          <InfoWindowF
            position={{
              lat: selectedResidence.wgs84lat,
              lng: selectedResidence.wgs84lon,
            }}
            onCloseClick={() => setSelectedResidence(null)}
          >
            <InfoContent>
              <InfoTitle>{selectedResidence.address}</InfoTitle>
              {selectedResidence.postal_area != null && (
                <InfoSubtitle>{selectedResidence.postal_area}</InfoSubtitle>
              )}
              <InfoDivider />
              <InfoGrid>
                {popupColumns
                  .filter((column) => column.name !== 'postal_area')
                  .map((column) => (
                    <Fragment key={column.name}>
                      <InfoLabel>{column.label}</InfoLabel>
                      <InfoValue>
                        {selectedResidence[column.name] ?? '–'}
                      </InfoValue>
                    </Fragment>
                  ))}
              </InfoGrid>
            </InfoContent>
          </InfoWindowF>
        )}
      </GoogleMap>
    </StyledMap>
  );
};

export default RegistryMap;
